// src/components/TempoTracker.jsx
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../supabaseClient";
import { usePresenca } from "../hooks/usePresenca";
import { useAtividades } from "../hooks/useAtividades";
import { useEquipeTempo } from "../hooks/useEquipeTempo";
import logoOnbox from "../assets/ob5.png";
import logoPip from "../assets/ob1.png";
import "./TempoTracker.css";

const pad = (n) => String(n).padStart(2, "0");

const fmt = (total) => {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

const fmtCurto = (total) => {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h > 0) return `${h}h ${pad(m)}min`;
  if (m > 0) return `${m}min`;
  return `${total}s`;
};

const fmtDec = (ms) => {
  const total = Math.floor(ms / 1000);
  const dec = Math.floor((ms % 1000) / 100);
  return `${fmt(total)}.${dec}`;
};

// ── Texto para atividade (usado no feed) ──
const textoAtividade = (a) => {
  const item = a.nome_item ? ` "${a.nome_item}"` : "";
  switch (a.tipo_acao) {
    case "criou_nota": return `criou a nota${item}`;
    case "concluiu_tarefa": return `concluiu a tarefa${item}`;
    case "anexou_arquivo": return `anexou${item}`;
    default: return `${a.tipo_acao.replace(/_/g, " ")}${item}`;
  }
};

function RelogioAnalogico({ ms, status }) {
  const s = (ms / 1000) % 60;
  const m = (ms / 60000) % 60;
  const h = (ms / 3600000) % 12;
  const rot = (deg) => `rotate(${deg} 50 50)`;
  return (
    <svg className={`tt-analog tt-analog--${status}`} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="47" className="tt-a-face" />
      {[...Array(12)].map((_, i) => (
        <line key={i} x1="50" y1="7" x2="50" y2={i % 3 === 0 ? 15 : 11} transform={rot(i * 30)} className="tt-a-tick" />
      ))}
      <line x1="50" y1="50" x2="50" y2="29" transform={rot(h * 30)} className="tt-a-hand tt-a-hour" />
      <line x1="50" y1="50" x2="50" y2="17" transform={rot(m * 6)} className="tt-a-hand tt-a-min" />
      <line x1="50" y1="56" x2="50" y2="12" transform={rot(s * 6)} className="tt-a-sec" />
      <circle cx="50" cy="50" r="2.5" className="tt-a-center" />
    </svg>
  );
}

function Avatar({ nome, src, status, voce }) {
  const ini = (nome || "?").trim()[0].toUpperCase();
  const primeiro = (nome || "").trim().split(" ")[0];
  const txt = status === "running" ? "trabalhando" : status === "paused" ? "pausado" : "online";
  return (
    <div className="tt-av-wrap" title={`${voce ? "Você" : nome} · ${txt}`}>
      <div className={`tt-av tt-av--${status}`}>
        <span>{ini}</span>
        {src && <img src={src} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      </div>
      <span className="tt-av-nome">{voce ? "Você" : primeiro}</span>
    </div>
  );
}

const diaLocal = (ms) => new Date(ms).toLocaleDateString("en-CA");

const fmtDia = (d) => {
  const [a, m, dia] = d.split("-");
  return new Date(a, m - 1, dia).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
};

const fmtHora = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

export default function TempoTracker({
  usuarioId,
  usuarioNome,
  usuarioAvatar,
  entityType,
  entityId,
  entityNome,
  setorNome,
}) {
  const storageKey = `tempo_${usuarioId}_${entityType}_${entityId}`;

  const [aberto, setAberto] = useState(false);
  const [status, setStatus] = useState("idle");
  const [segStart, setSegStart] = useState(null);
  const [acumulado, setAcumulado] = useState(0);
  const [, setTick] = useState(0);
  const [historico, setHistorico] = useState([]);
  const [ocupado, setOcupado] = useState(false);
  const [carregado, setCarregado] = useState(false);
  const wrapRef = useRef(null);

  // ── Janela flutuante (Document Picture-in-Picture) ──
  const [pipWin, setPipWin] = useState(null);
  const [pipLista, setPipLista] = useState(false);

  // ── Presença (quem mais está na mesma entidade) — com `desde` ──
  const outros = usePresenca({
    usuarioId,
    nome: usuarioNome,
    avatar: usuarioAvatar,
    desde: segStart,
    entityType,
    entityId,
    status,
  });

  // ── Eventos recentes da equipe (tempo_registros) ──
  const { eventos, perfis } = useEquipeTempo(entityType, entityId);

  // ── Atividade recente da entidade ──
  const { atividades, perfisAtiv } = useAtividades(entityType, entityId);

  // ── Nome exibido: usa a prop ou busca pelo id ──
  const [nomeBuscado, setNomeBuscado] = useState("");

  useEffect(() => {
    if (entityNome || !entityId) return;
    const tabela =
      entityType === "project" ? "projects" :
      entityType === "setor"   ? "setores" :
      "projects";

    supabase
      .from(tabela)
      .select("name")
      .eq("id", entityId)
      .maybeSingle()
      .then(({ data }) => setNomeBuscado(data?.name || ""));
  }, [entityId, entityNome, entityType]);

  const nomeExibido = entityNome || nomeBuscado;

  useEffect(() => {
    if (!usuarioId || !entityId) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const s = JSON.parse(raw);
        setStatus(s.status || "idle");
        setSegStart(s.segStart || null);
        setAcumulado(s.acumulado || 0);
      }
    } catch {}
    setCarregado(true);
  }, [storageKey]);

  useEffect(() => {
    if (!carregado) return;
    try {
      if (status === "idle") localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, JSON.stringify({ status, segStart, acumulado }));
    } catch {}
  }, [status, segStart, acumulado, carregado, storageKey]);

  // ── Timer: roda enquanto EU estiver rodando ou alguém da equipe estiver rodando ──
  const alguemRodando = outros.some((p) => p.status === "running");

  useEffect(() => {
    if (status !== "running" && !alguemRodando) return;
    const w = pipWin || window;
    const t = w.setInterval(() => setTick((v) => v + 1), pipWin ? 100 : 1000);
    return () => w.clearInterval(t);
  }, [status, alguemRodando, pipWin]);

  // ── Fecha painel ao clicar fora (mas não se o PiP estiver aberto) ──
  useEffect(() => {
    if (!aberto || pipWin) return;
    const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [aberto, pipWin]);

  const buscarHistorico = async () => {
    if (!usuarioId || !entityId) return;
    const { data } = await supabase
      .from("tempo_registros")
      .select("id, dia, inicio, fim, duracao_segundos, acao")
      .eq("user_id", usuarioId)
      .eq("entidade_tipo", entityType)
      .eq("entidade_id", String(entityId))
      .order("inicio", { ascending: false })
      .limit(100);
    setHistorico(data || []);
  };

  useEffect(() => { if (aberto) buscarHistorico(); }, [aberto, storageKey]);

  const registrarSegmento = async (acao, fimMs) => {
    const dur = Math.floor((fimMs - segStart) / 1000);
    if (dur < 1) return 0;
    const { error } = await supabase.from("tempo_registros").insert([{
      user_id: usuarioId,
      entidade_tipo: entityType,
      entidade_id: String(entityId),
      dia: diaLocal(segStart),
      inicio: new Date(segStart).toISOString(),
      fim: new Date(fimMs).toISOString(),
      duracao_segundos: dur,
      acao,
    }]);
    if (error) { alert("Erro ao salvar tempo: " + error.message); return 0; }
    return dur;
  };

  const iniciar = () => {
    setSegStart(Date.now());
    setStatus("running");
  };

  const pausar = async () => {
    if (ocupado || status !== "running") return;
    setOcupado(true);
    const dur = await registrarSegmento("pausa", Date.now());
    setAcumulado((a) => a + dur);
    setSegStart(null);
    setStatus("paused");
    await buscarHistorico();
    setOcupado(false);
  };

  const parar = async () => {
    if (ocupado || status === "idle") return;
    setOcupado(true);
    if (status === "running") await registrarSegmento("parada", Date.now());
    setSegStart(null);
    setAcumulado(0);
    setStatus("idle");
    await buscarHistorico();
    setOcupado(false);
  };

  const excluirRegistro = async (id) => {
    if (!window.confirm("Excluir este registro?")) return;
    await supabase.from("tempo_registros").delete().eq("id", id);
    buscarHistorico();
  };

  // ── Abrir janela flutuante (PiP) ──
  const abrirFlutuante = async () => {
    if (!("documentPictureInPicture" in window)) {
      alert("Seu navegador não suporta janela flutuante. Use Chrome ou Edge.");
      return;
    }
    const win = await window.documentPictureInPicture.requestWindow({ width: 440, height: 130 });
    win.document.body.style.margin = "0";

    [...document.styleSheets].forEach((sheet) => {
      try {
        const css = [...sheet.cssRules].map((r) => r.cssText).join("");
        const style = document.createElement("style");
        style.textContent = css;
        win.document.head.appendChild(style);
      } catch {
        if (sheet.href) {
          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = sheet.href;
          win.document.head.appendChild(link);
        }
      }
    });

    win.addEventListener("pagehide", () => { setPipWin(null); setPipLista(false); });
    setPipWin(win);
  };

  if (!usuarioId || !entityId) return null;

  const elapsed = acumulado + (status === "running" && segStart ? Math.floor((Date.now() - segStart) / 1000) : 0);
  const elapsedMs = acumulado * 1000 + (status === "running" && segStart ? Date.now() - segStart : 0);

  const porDia = historico.reduce((acc, r) => {
    if (!acc[r.dia]) acc[r.dia] = { total: 0, itens: [] };
    acc[r.dia].total += r.duracao_segundos;
    acc[r.dia].itens.push(r);
    return acc;
  }, {});
  const dias = Object.keys(porDia).sort((a, b) => (a < b ? 1 : -1));

  // ── FEED da equipe: presença viva + eventos recentes + atividades ──
  const primeiro = (n) => (n || "Alguém").trim().split(" ")[0];
  const todosPerfis = { ...perfis, ...perfisAtiv };
  const nomeDe = (id) => primeiro(todosPerfis[id]?.nome || todosPerfis[id]?.nickname);
  const quem = (id) => (id === usuarioId ? "Você" : nomeDe(id));
  const agora = Date.now();

  const vivos = [
    ...outros
      .filter((p) => p.status === "running" && p.desde)
      .map((p) => ({
        chave: `vivo-${p.id}`,
        txt: `${primeiro(p.nome)} está trabalhando há ${fmtCurto(Math.floor((agora - p.desde) / 1000))}`,
      })),
    ...outros
      .filter((p) => p.status === "paused")
      .map((p) => ({ chave: `pausa-${p.id}`, txt: `${primeiro(p.nome)} está pausado` })),
  ].map((x) => ({ ...x, vivo: true }));

  const passados = [
    ...eventos.map((r) => ({
      chave: `t-${r.id}`,
      quando: new Date(r.fim).getTime(),
      txt: `${quem(r.user_id)} ${r.acao === "pausa" ? "pausou" : "parou"} · ${fmtCurto(r.duracao_segundos)} · ${fmtHora(r.fim)}`,
    })),
    ...atividades.map((a) => ({
      chave: `a-${a.id}`,
      quando: new Date(a.created_at).getTime(),
      txt: `${quem(a.user_id)} ${textoAtividade(a)} · ${fmtHora(a.created_at)}`,
    })),
  ]
    .sort((a, b) => b.quando - a.quando)
    .map((x) => ({ ...x, vivo: false }));

  const feed = [...vivos, ...passados];

  const alternarLista = () => {
    const novo = !pipLista;
    setPipLista(novo);
    pipWin?.resizeTo(440, novo ? 240 : 130);
  };

  // ── Conteúdo do painel normal ──
  const conteudoPainel = (
    <>
      <div className="tt-topo">
        <img className="tt-logo" src={logoOnbox} alt="Onbox" />

        <div className="txt-topo">
        <span className="tt-onde" title={`${setorNome ? setorNome + " › " : ""}${nomeExibido}`}>
          {setorNome ? `${setorNome} › ` : ""}{nomeExibido}
        </span>
      </div></div>

      <div className="tt-display">
        <span className={`tt-status tt-status--${status}`}>
          {status === "running" ? "Em andamento" : status === "paused" ? "Pausado" : "Parado"}
        </span>
        <span className="tt-clock">{fmt(elapsed)}</span>
      </div>

      <div className="tt-actions">
        {status !== "running" && (
          <button type="button" className="tt-action tt-action--start" onClick={iniciar}>
            <span className="material-symbols-outlined">play_arrow</span>
            <span className="tt-action-txt">{status === "paused" ? "Retomar" : "Iniciar"}</span>
          </button>
        )}
        {status === "running" && (
          <button type="button" className="tt-action tt-action--pause" onClick={pausar} disabled={ocupado}>
            <span className="material-symbols-outlined">pause</span>
            <span className="tt-action-txt">Pausar</span>
          </button>
        )}
        <button type="button" className="tt-action tt-action--stop" onClick={parar} disabled={ocupado || status === "idle"}>
          <span className="material-symbols-outlined">stop</span>
          <span className="tt-action-txt">Parar</span>
        </button>
      </div>

      {outros.length > 0 && (
        <div className="tt-pessoas">
          {outros.map((p) => (
            <span
              key={p.id}
              className={`tt-pessoa tt-pessoa--${p.status}`}
              title={`${p.nome} · ${p.status === "running" ? "trabalhando" : p.status === "paused" ? "pausado" : "online"}`}
            >
              {(p.nome || "?").trim()[0].toUpperCase()}
            </span>
          ))}
        </div>
      )}

      <div className="tt-feed-titulo">Equipe</div>
      <div className="tt-feed">
        {feed.length === 0 && <div className="tt-vazio">Sem atividade da equipe ainda.</div>}
        {feed.slice(0, 8).map((f) => (
          <div key={f.chave} className={`tt-feed-item ${f.vivo ? "tt-feed-item--vivo" : ""}`}>{f.txt}</div>
        ))}
      </div>

      <div className="tt-hist-titulo">Histórico</div>
      <div className="tt-hist">
        {dias.length === 0 && <div className="tt-vazio">Nenhum registro ainda.</div>}
        {dias.map((d) => (
          <div key={d} className="tt-dia">
            <div className="tt-dia-head">
              <span>{fmtDia(d)}</span>
              <strong>{fmtCurto(porDia[d].total)}</strong>
            </div>
            {porDia[d].itens.map((r) => (
              <div key={r.id} className="tt-item">
                <span className="tt-item-hora">{fmtHora(r.inicio)} – {fmtHora(r.fim)}</span>
                <span className="tt-item-dur">{fmtCurto(r.duracao_segundos)}</span>
                <span className={`tt-item-acao tt-item-acao--${r.acao}`}>{r.acao}</span>
                <button type="button" onClick={() => excluirRegistro(r.id)} title="Excluir">✕</button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );

  // ── Conteúdo compacto do PiP ──
  const conteudoPip = (
    <div className="tt-pip-root">
      <div className="tt-pip">
        <img className="tt-pip-logo" src={logoPip} alt="Onbox" />

        <div className="tt-pip-clock">
          <RelogioAnalogico ms={elapsedMs} status={status} />
          <span className="tt-pip-time">{fmtDec(elapsedMs)}</span>
        </div>

        <div className="tt-pip-info">
          <span className="tt-pip-onde" title={nomeExibido}>
            {setorNome ? `${setorNome} › ` : ""}{nomeExibido}
          </span>
          <div className="tt-pip-btns">
            {status === "running" ? (
              <button type="button" className="tt-pip-btn" onClick={pausar} disabled={ocupado} title="Pausar">
                <span className="material-symbols-outlined">pause</span>
              </button>
            ) : (
              <button type="button" className="tt-pip-btn" onClick={iniciar} title={status === "paused" ? "Retomar" : "Iniciar"}>
                <span className="material-symbols-outlined">play_arrow</span>
              </button>
            )}
            <button type="button" className="tt-pip-btn" onClick={parar} disabled={ocupado || status === "idle"} title="Parar">
              <span className="material-symbols-outlined">stop</span>
            </button>
          </div>
        </div>

        <div className="tt-pip-sep" />

        <div className="tt-pip-users">
          <Avatar voce nome={usuarioNome} src={usuarioAvatar} status={status} />
          {outros.slice(0, 4).map((p) => (
            <Avatar key={p.id} nome={p.nome} src={p.avatar} status={p.status} />
          ))}
          {outros.length > 4 && <span className="tt-pip-mais">+{outros.length - 4}</span>}
        </div>
      </div>

      <button type="button" className="tt-pip-feed" onClick={alternarLista} title="Histórico da equipe">
        <span className={`tt-feed-dot ${feed[0]?.vivo ? "tt-feed-dot--vivo" : ""}`} />
        <span className="tt-pip-feed-txt">{feed[0]?.txt || "Sem atividade da equipe ainda"}</span>
        <span className="material-symbols-outlined">{pipLista ? "expand_less" : "expand_more"}</span>
      </button>

      {pipLista && (
        <div className="tt-pip-lista">
          {feed.slice(0, 10).map((f) => (
            <div key={f.chave} className={`tt-feed-item ${f.vivo ? "tt-feed-item--vivo" : ""}`}>{f.txt}</div>
          ))}
        </div>
      )}
    </div>
  );

  return (
        <div className={`tt-wrap tt-wrap--${status}`} ref={wrapRef}>
      <div className={`tt-line ${status === "running" ? "tt-line--on" : ""} ${status === "paused" ? "tt-line--paused" : ""}`} />
      <div className="tt-cauda" />
      <button
        type="button"
        className={`tt-btn ${status !== "idle" ? "tt-btn--ativo" : ""}`}
        onClick={() => setAberto((v) => !v)}
        title="Contabilizador de tempo"
      >
        <span className="material-symbols-outlined">schedule</span>
        {status !== "idle" && <span className="tt-btn-time">{fmt(elapsed)}</span>}
      </button>

      {aberto && !pipWin && (
        <div className="tt-panel">
          <button type="button" className="tt-pop" onClick={abrirFlutuante} title="Abrir janela flutuante">
            <span className="material-symbols-outlined">picture_in_picture_alt</span>
          </button>
          {conteudoPainel}
        </div>
      )}

      {pipWin && createPortal(conteudoPip, pipWin.document.body)}
    </div>
  );
}